import fuzzysort from 'fuzzysort';
import {isString} from 'lodash';
import React, {ReactNode} from 'react';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  isSearchModifierToken,
  isValidSearchModifierToken,
  SearchQueryPart,
  splitSearchQuery,
} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {HighlightRangeModel} from '../../../../../models/api/conversationHighlightModel';
import {Highlight} from '../../../cells/header/search/highlight/highlight';
import {SearchSuggestionsSearchToken} from './searchSuggestionsSearchToken';

/*
 * Helpers.
 */

export function renderHighlightedPart(
  part: ReactNode,
  partPlaceholder: ReactNode,
  fuzzyQuery: string,
  shouldDisplaySearchTokens: boolean = false,
  isBold: boolean = false,
): ReactNode {
  if (!isString(part)) {
    return part;
  }

  // Compute ranges for this part.
  const highlightRanges = computeHighlightRanges(part, fuzzyQuery);

  // If we should not use search tokens, we could simply highlight the part.
  if (!shouldDisplaySearchTokens) {
    return <Highlight text={part} ranges={highlightRanges} fontWeight={isBold ? 'bold' : 'semibold'} />;
  }

  // If we have a placeholder, we could simply highlight the part within a search token.
  if (isDefined(partPlaceholder)) {
    return (
      <SearchSuggestionsSearchToken
        value={<Highlight text={part} ranges={highlightRanges} />}
        placeholder={partPlaceholder}
      />
    );
  }

  // Otherwise, merge query parts and highlight them (with search tokens if they contain a modifier token).
  const mergedQueryParts = splitAndMergeQueryParts(part);

  return mergedQueryParts.map(({token, text, startOffset, endOffset}) => {
    // Find highlight ranges that belongs to this query part and update them.
    const relatedHighlightRanges = highlightRanges
      .filter(({start, length}) => start >= startOffset && start + length <= endOffset)
      .map(({start, length}) => ({
        start: start - startOffset,
        length,
      }));

    if (!token || !isSearchModifierToken(token) || !isValidSearchModifierToken(token)) {
      return (
        <Highlight
          key={startOffset}
          text={text}
          ranges={relatedHighlightRanges}
          fontWeight={isBold ? 'bold' : 'semibold'}
        />
      );
    }

    return (
      <SearchSuggestionsSearchToken
        key={startOffset}
        value={<Highlight text={text} ranges={relatedHighlightRanges} />}
      />
    );
  });
}

function computeHighlightRanges(text: string, fuzzyQuery: string): ReadonlyArray<HighlightRangeModel> {
  const fuzzyIndexes: Fuzzysort.Result['indexes'] = [];

  // Split `fuzzyQuery` on spaces and dots.
  const splitFuzzyQueries = fuzzyQuery.split(/\s|\./);

  // For each split fuzzy query, find the indexes of the text that match.
  for (const splitFuzzyQuery of splitFuzzyQueries) {
    const [result] = fuzzysort.go(splitFuzzyQuery, [text]);
    fuzzyIndexes.push(...(result?.indexes ?? []));
  }

  if (fuzzyIndexes.length === 0) {
    return [];
  }

  return fuzzyHighlightToHighlightRanges(fuzzyIndexes);
}

/**
 * Transform an array of indexes to highlight : [1, 2, 3, 5, 6, 8] (fuzzysort library format)
 * into an array of highlight ranges: [{start: 1,length: 3},{start: 5,length: 2},{start: 8,length: 1}] (<Highlight> component format)
 */
export function fuzzyHighlightToHighlightRanges(
  fuzzyIndexes: Fuzzysort.Result['indexes'],
): ReadonlyArray<HighlightRangeModel> {
  // Init
  const highlightRanges: Array<HighlightRangeModel> = [];

  let currentIndex = 0;
  let start = fuzzyIndexes[currentIndex];
  let length = 1;
  while (currentIndex < fuzzyIndexes.length) {
    // Increment length while next value is equal to current value + 1
    while (fuzzyIndexes[currentIndex] + 1 === fuzzyIndexes[++currentIndex]) {
      length++;
    }

    // Push the new highlight range
    highlightRanges.push({start, length});

    // Init next highlight range
    start = fuzzyIndexes[currentIndex];
    length = 1;
  }

  return highlightRanges;
}

export function splitAndMergeQueryParts(str: string): ReadonlyArray<SearchQueryPart> {
  const queryParts = splitSearchQuery(str);

  const mergedQueryParts: Array<SearchQueryPart> = [];

  // For each query part, check if we could merge with the previous one.
  for (const queryPart of queryParts) {
    const {token, text, startOffset, endOffset} = queryPart;

    // If we have a modifier token, we never want to merge.
    if (token && isSearchModifierToken(token)) {
      mergedQueryParts.push(queryPart);
      continue;
    }

    const lastMergedQueryPartIndex = mergedQueryParts.length - 1;
    const previousMergedQueryPart = mergedQueryParts[lastMergedQueryPartIndex];

    // If the previous query part does not exist or contains a modifier token, add the query part to the array.
    if (
      !previousMergedQueryPart ||
      (previousMergedQueryPart.token && isSearchModifierToken(previousMergedQueryPart.token))
    ) {
      mergedQueryParts.push({
        text,
        startOffset,
        endOffset,
        // We don't care about the token as we're using `text` for simple tokens.
        token: undefined,
      });
      continue;
    }

    // Otherwise, merge the current query part with the previous one.
    mergedQueryParts[lastMergedQueryPartIndex] = {
      // Concatenate texts.
      text: previousMergedQueryPart.text + text,
      // Keep the original start offset.
      startOffset: previousMergedQueryPart.startOffset,
      // Update the end offset
      endOffset,
      // We don't care about the token as we'll use text.
      token: undefined,
    };
  }

  return mergedQueryParts;
}
